import React, { useState, useEffect } from 'react'
import { ArrowLeft, CheckCircle, Users, Loader2 } from 'lucide-react'
import { EnrollmentApi } from '@/features/enrollment'
import HourStatsAnalysis from '../gaming-addiction/components/HourStatsAnalysis'
import AnotherChildEnrollment from './AnotherChildEnrollment'
import ReferralManagement from './ReferralManagement'
import ReferralTracking from './ReferralTracking'
import JobApplication from './JobApplication'
import WorkflowSummary from './WorkflowSummary'
import type { Step10FollowUpProps, HourStats, ValidationErrors } from '@/features/enrollment'

export default function Step10FollowUp({ enrollmentData, updateEnrollmentData, handleNavigate, handleSimpleNavigate, onComplete, onStartNextChild, isCompletedMode, isNavigating, isCompleting }: Step10FollowUpProps) {
  const [newReferral, setNewReferral] = useState('')
  const [creatingAccounts, setCreatingAccounts] = useState(false)
  const [accountsCreated, setAccountsCreated] = useState<string[]>([])
  const [referralTracking, setReferralTracking] = useState<any[]>([])
  const [showReferralTracking, setShowReferralTracking] = useState(false)
  const [showJobApplication, setShowJobApplication] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [hourStats, setHourStats] = useState<HourStats>({
    totalHours: 0,
    eligibleGamers: 0,
    remainingGamers: 0
  })

  const handleBack = () => {
    if (handleSimpleNavigate) {
      handleSimpleNavigate(7)
    } else if (handleNavigate) {
      handleNavigate(7)
    }
  }

  const referralEmails: string[] = Array.isArray((enrollmentData as any).referralEmails)
    ? (enrollmentData as any).referralEmails
    : ((enrollmentData.referralEmail ? [enrollmentData.referralEmail] : []) as string[])

  // Calculate gamertag hour statistics
  useEffect(() => {
    const calculateHourStats = async () => {
      try {
       
        
        // 1. Get total hours from gamertag verification API
        let totalHours = 0
        let currentGamertags: string[] = []
        
        // Check Xbox
        if (enrollmentData.platforms?.xbox?.gamerTag && 
            enrollmentData.platforms?.xbox?.verificationResult?.totalHoursPlayed) {
          totalHours += enrollmentData.platforms.xbox.verificationResult.totalHoursPlayed
          currentGamertags.push(enrollmentData.platforms.xbox.gamerTag.toLowerCase())
        }
        
        // Check PlayStation
        if (enrollmentData.platforms?.playstation?.gamerTag && 
            enrollmentData.platforms?.playstation?.verificationResult?.totalHoursPlayed) {
          totalHours += enrollmentData.platforms.playstation.verificationResult.totalHoursPlayed
          currentGamertags.push(enrollmentData.platforms.playstation.gamerTag.toLowerCase())
        }
        
        // Check Steam
        if (enrollmentData.platforms?.steam?.gamerTag && 
            enrollmentData.platforms?.steam?.verificationResult?.totalHoursPlayed) {
          totalHours += enrollmentData.platforms.steam.verificationResult.totalHoursPlayed
          currentGamertags.push(enrollmentData.platforms.steam.gamerTag.toLowerCase())
        }
        
        // Fallback for testing if no real hours found
        if (totalHours === 0) {
          totalHours = 11640 // Mock data for testing
          currentGamertags = ['testgamertag123']
        }
        
        // 2. Query database to count how many times these gamertags are used
        let gamertagUsageCount = 0
        try {
          const { EnrollmentApi } = await import('@/features/enrollment')
          const allSessionsResult = await EnrollmentApi.listSessions()
          const allSessions = allSessionsResult.sessions
          
          
          // Count all sessions (COMPLETED, PENDING, IN_PROGRESS) that use the same gamertags
          const sessionsUsingGamertag = allSessions.filter((session: any) => {
            const sessionData = session.data as any
            const sessionGamertags: string[] = []
            
            // Extract gamertags from session data
            if (sessionData?.step1?.platforms?.xbox?.gamerTag) {
              sessionGamertags.push(sessionData.step1.platforms.xbox.gamerTag.toLowerCase())
            }
            if (sessionData?.step1?.platforms?.playstation?.gamerTag) {
              sessionGamertags.push(sessionData.step1.platforms.playstation.gamerTag.toLowerCase())
            }
            if (sessionData?.step1?.platforms?.steam?.gamerTag) {
              sessionGamertags.push(sessionData.step1.platforms.steam.gamerTag.toLowerCase())
            }
            
            // Also check root platforms data
            if (sessionData?.platforms?.xbox?.gamerTag) {
              sessionGamertags.push(sessionData.platforms.xbox.gamerTag.toLowerCase())
            }
            if (sessionData?.platforms?.playstation?.gamerTag) {
              sessionGamertags.push(sessionData.platforms.playstation.gamerTag.toLowerCase())
            }
            if (sessionData?.platforms?.steam?.gamerTag) {
              sessionGamertags.push(sessionData.platforms.steam.gamerTag.toLowerCase())
            }
            
            // Check if any gamertag matches
            const hasMatchingGamertag = currentGamertags.some(tag => sessionGamertags.includes(tag))
            
            return hasMatchingGamertag
          })
          
          gamertagUsageCount = sessionsUsingGamertag.length
          
        } catch (error) {
          console.error('Failed to query database for gamertag usage:', error)
        }
        
        // 3. Calculate remaining gamers
        const eligibleGamers = Math.floor(totalHours / 1100)
        const remainingGamers = Math.max(0, eligibleGamers - gamertagUsageCount)
        
        
        setHourStats({
          totalHours,
          eligibleGamers,
          remainingGamers
        })
      
      } catch (error) {
        console.error('Error calculating hour stats:', error)
        setHourStats({
          totalHours: 0,
          eligibleGamers: 0,
          remainingGamers: 0
        })
      }
    }

    calculateHourStats()
  }, [enrollmentData])

  const addReferral = () => {
    const email = newReferral.trim()
    if (!email) return
    const next = Array.from(new Set([...(referralEmails || []), email]))
    updateEnrollmentData('referralEmails' as any, next)
    setNewReferral('')
  }

  const removeReferral = (email: string) => {
    const next = (referralEmails || []).filter((e) => e !== email)
    updateEnrollmentData('referralEmails' as any, next)
  }

  const createUserAccountsForReferrals = async () => {
    if (!referralEmails || referralEmails.length === 0) {
      alert('No referral emails to create accounts for.')
      return
    }

    setCreatingAccounts(true)
    const createdEmails: string[] = []

    try {
      for (const email of referralEmails) {
        try {
          await EnrollmentApi.createUserAccountForReferral(email)
          createdEmails.push(email)
        } catch (error) {
          console.error(`Failed to create account for ${email}:`, error)
          // Continue with other emails even if one fails
        }
      }

      setAccountsCreated(createdEmails)
      
      if (createdEmails.length > 0) {
        alert(`Successfully created user accounts for ${createdEmails.length} referral(s). Login details have been sent to their emails.`)
      }
      
      if (createdEmails.length < referralEmails.length) {
        alert(`Some accounts could not be created. ${createdEmails.length}/${referralEmails.length} accounts were created successfully.`)
      }
    } catch (error) {
      console.error('Error creating user accounts:', error)
      alert('Failed to create user accounts. Please try again.')
    } finally {
      setCreatingAccounts(false)
    }
  }

  // Track referral progress
  const trackReferralProgress = async () => {
    try {
      setShowReferralTracking(true)
      // This would call an API to get referral progress
      // For now, using mock data
      const mockTrackingData = referralEmails.map(email => ({
        email,
        status: Math.random() > 0.5 ? 'completed' : 'in_progress',
        step: Math.floor(Math.random() * 10) + 1,
        lastActivity: new Date().toISOString()
      }))
      setReferralTracking(mockTrackingData)
    } catch (error) {
      console.error('Error tracking referrals:', error)
    }
  }

  // Apply for job (become sales agent)
  const applyForJob = async () => {
    try {
      setShowJobApplication(true)
      // This would initiate the job application process
      // For now, just show the form
    } catch (error) {
      console.error('Error applying for job:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg lg:text-xl font-semibold text-accent-900">Follow-up Options</h3>
            <p className="text-sm text-accent-600">Additional enrollment and referral opportunities</p>
          </div>
        </div>

        <div className="space-y-6">
          <HourStatsAnalysis hourStats={hourStats} />

          <AnotherChildEnrollment
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            hourStats={hourStats}
            onStartNextChild={onStartNextChild}
          />

          <ReferralManagement
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            referralEmails={referralEmails}
            newReferral={newReferral}
            setNewReferral={setNewReferral}
            addReferral={addReferral}
            removeReferral={removeReferral}
            createUserAccountsForReferrals={createUserAccountsForReferrals}
            creatingAccounts={creatingAccounts}
            accountsCreated={accountsCreated}
          />
        </div>

        <ReferralTracking
          showReferralTracking={showReferralTracking}
          referralTracking={referralTracking}
          trackReferralProgress={trackReferralProgress}
        />

        <JobApplication
          showJobApplication={showJobApplication}
          applyForJob={applyForJob}
        />

        <WorkflowSummary />
      </div>

      <div className="flex justify-between">
        <button
          className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
          onClick={handleBack}
          disabled={isNavigating}
        >
          <>
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </>
        </button>
        <button
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 lg:px-8 rounded-lg flex items-center space-x-2 transition-colors min-w-[150px] justify-center"
          onClick={async () => {
            if (enrollmentData.submitAnotherChild === true) {
              // Start new application with cloned data
              if (onStartNextChild) onStartNextChild()
            } else {
              // Complete current enrollment and start fresh
              if (onComplete) onComplete()
              else await handleNavigate(0)
            }
            
            // Trigger status refresh for pending applications
            window.dispatchEvent(new CustomEvent('pending:updated'))
          }}
          disabled={isCompleting}
        >
          {isCompleting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Completing...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>
                {enrollmentData.submitAnotherChild === true 
                  ? 'Start New Application' 
                  : 'Complete Enrollment'
                }
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

